/**
 * Cores de função do diagrama — o que cada cor quer dizer.
 *
 * É a legenda do desenho: `#C7E4FB` é "Controles da Transmissão e Tração". O
 * diagrama pinta o componente com a cor, e o cliente lê o significado — em
 * português, inglês ou espanhol, porque o conteúdo é publicado nos três.
 *
 * **A chave é a própria cor**, não um id. Isso tem duas consequências que a
 * tela precisa respeitar:
 *
 *  - editar o texto é seguro e sai por `PUT`;
 *  - **trocar a cor é outra coisa**: sai por `PATCH .../trocar`, muda a chave, e
 *    repinta todo componente que a usa em todos os projetos. É a operação que
 *    merece confirmação, e a tela dá a ela um lugar separado do formulário.
 */

import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto, obter } from "@/api/cliente"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"

type CorDeFuncao = {
  cor: string
  titulo: string
  descricao: string
  descricaoEn: string
  descricaoEs: string
  classFill: string
}

export function CoresDeFuncao() {
  const qc = useQueryClient()
  const [escolhida, setEscolhida] = useState<CorDeFuncao | null>(null)

  const lista = useQuery({
    queryKey: ["cores-funcao"] as const,
    queryFn: () => obter<CorDeFuncao[]>("api/cores-funcao", {}),
  })

  const salvar = useMutation({
    mutationFn: async (c: CorDeFuncao) => {
      await api.put(`api/cores-funcao/${encodeURIComponent(c.cor)}`, {
        titulo: c.titulo, descricao: c.descricao,
        descricaoEn: c.descricaoEn, descricaoEs: c.descricaoEs,
      }, foraDoProjeto())
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cores-funcao"] }),
  })

  const trocar = useMutation({
    mutationFn: async ({ de, para }: { de: string; para: string }) => {
      await api.patch(`api/cores-funcao/${encodeURIComponent(de)}/trocar`,
        { corNova: para }, foraDoProjeto())
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cores-funcao"] })
      setEscolhida(null)
    },
  })

  const cores = lista.data ?? []

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <AvisoDeDadoGlobal oQueMuda="Trocar uma cor repinta o componente em todos os diagramas de todos os projetos" />

      <div
        className="barra"
        style={{
          height: 48, flex: "0 0 48px", display: "flex", alignItems: "center", gap: 12,
          padding: "0 18px", background: "var(--surface)", borderBottom: "1px solid var(--line)",
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 600 }}>Cores de função</span>
        <span style={{ fontSize: 12, color: "var(--txt2)" }}>
          A legenda do diagrama — o que cada cor quer dizer, nos três idiomas.
        </span>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <div style={{ flex: 1, minWidth: 0, overflowY: "auto", padding: 18 }}>
          {lista.isPending ? (
            <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando…</p>
          ) : lista.error ? (
            <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
              {lista.error instanceof ErroDaApi ? lista.error.message : "Não foi possível ler as cores."}
            </p>
          ) : (
            <div style={{
              display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 10,
            }}>
              {cores.map((c) => (
                <button
                  key={c.cor}
                  onClick={() => setEscolhida(c)}
                  aria-pressed={escolhida?.cor === c.cor}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 11px",
                    borderRadius: 10, textAlign: "left", background: "var(--surface)",
                    border: `1px solid ${escolhida?.cor === c.cor ? "var(--brand)" : "var(--line2)"}`,
                    boxShadow: escolhida?.cor === c.cor ? "inset 2px 0 0 var(--brand)" : undefined,
                  }}
                >
                  {/* A amostra tem borda própria: cor clara sobre fundo claro
                      (e escura sobre escuro) desapareceria sem ela. */}
                  <span
                    aria-hidden="true"
                    style={{
                      flex: "0 0 26px", width: 26, height: 26, borderRadius: 6,
                      background: c.cor, border: "1px solid var(--line2)",
                    }}
                  />
                  <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600 }}>{c.titulo || "(sem título)"}</span>
                    <span className="mono" style={{ fontSize: 10.5, color: "var(--txt3)" }}>{c.cor}</span>
                    {c.descricao && (
                      <span style={{ fontSize: 11, color: "var(--txt2)", lineHeight: 1.4 }}>
                        {c.descricao}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {escolhida && (
          <PainelDaCor
            cor={escolhida}
            salvando={salvar.isPending}
            trocando={trocar.isPending}
            erro={salvar.error ?? trocar.error}
            aoSalvar={(c) => salvar.mutate(c)}
            aoTrocar={(nova) => trocar.mutate({ de: escolhida.cor, para: nova })}
            aoFechar={() => setEscolhida(null)}
          />
        )}
      </div>

      <BarraDeEstado>
        <span>
          <b className="num" style={{ color: "var(--txt)" }}>{cores.length}</b> cores
        </span>
        <span style={{ color: "var(--acao-on-soft)" }}>
          Isto vale para todos os diagramas de todos os projetos.
        </span>
      </BarraDeEstado>
    </div>
  )
}

function PainelDaCor({ cor, salvando, trocando, erro, aoSalvar, aoTrocar, aoFechar }: {
  cor: CorDeFuncao
  salvando: boolean
  trocando: boolean
  erro: unknown
  aoSalvar: (c: CorDeFuncao) => void
  aoTrocar: (nova: string) => void
  aoFechar: () => void
}) {
  const [c, setC] = useState(cor)
  const [ancora, setAncora] = useState(cor.cor)
  const [novaCor, setNovaCor] = useState("")
  const [trocandoAberto, setTrocandoAberto] = useState(false)

  if (cor.cor !== ancora) {
    setAncora(cor.cor)
    setC(cor)
    setNovaCor("")
    setTrocandoAberto(false)
  }

  return (
    <aside
      className="gaveta"
      style={{
        flex: "0 0 360px", width: 360, borderLeft: "1px solid var(--line2)",
        background: "var(--surface)", display: "flex", flexDirection: "column", minHeight: 0,
      }}
    >
      <div style={{
        flex: "0 0 44px", display: "flex", alignItems: "center", gap: 9,
        padding: "0 14px", borderBottom: "1px solid var(--line)",
      }}>
        <span aria-hidden="true" style={{
          flex: "0 0 18px", width: 18, height: 18, borderRadius: 5,
          background: c.cor, border: "1px solid var(--line2)",
        }} />
        <span className="mono" style={{ flex: 1, fontSize: 12.5, fontWeight: 600 }}>{c.cor}</span>
        <button onClick={aoFechar} aria-label="Fechar o painel"
                style={{ display: "flex", color: "var(--txt3)", padding: 4 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"
               style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round" }}>
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 14,
                    display: "flex", flexDirection: "column", gap: 11 }}>
        <Campo rotulo="Título">
          <input value={c.titulo} onChange={(e) => setC({ ...c, titulo: e.target.value })} style={entrada} />
        </Campo>
        <Campo rotulo="Descrição (português)">
          <textarea value={c.descricao} rows={2}
                    onChange={(e) => setC({ ...c, descricao: e.target.value })}
                    style={{ ...entrada, height: "auto", padding: "7px 9px", resize: "vertical" }} />
        </Campo>
        <Campo rotulo="Descrição (inglês)">
          <textarea value={c.descricaoEn} rows={2}
                    onChange={(e) => setC({ ...c, descricaoEn: e.target.value })}
                    style={{ ...entrada, height: "auto", padding: "7px 9px", resize: "vertical" }} />
        </Campo>
        <Campo rotulo="Descrição (espanhol)">
          <textarea value={c.descricaoEs} rows={2}
                    onChange={(e) => setC({ ...c, descricaoEs: e.target.value })}
                    style={{ ...entrada, height: "auto", padding: "7px 9px", resize: "vertical" }} />
        </Campo>

        <button
          onClick={() => aoSalvar(c)}
          disabled={salvando}
          style={{
            alignSelf: "flex-start", height: 32, padding: "0 13px", borderRadius: 7,
            background: "var(--brand)", color: "#FFFFFF", fontSize: 12.5, fontWeight: 600,
          }}
        >
          {salvando ? "Salvando…" : "Salvar os textos"}
        </button>

        {/* Trocar a cor fica separado do formulário de propósito: é a única
            operação daqui que repinta o desenho, e misturá-la com "salvar o
            texto" faria uma acontecer por causa da outra. */}
        <div style={{
          marginTop: 6, padding: "11px 12px", borderRadius: 9,
          background: "var(--acao-soft)", color: "var(--acao-on-soft)", border: "1px solid var(--acao)",
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          <b style={{ fontSize: 12 }}>Trocar a cor</b>
          <span style={{ fontSize: 11, lineHeight: 1.55 }}>
            A cor <b>é a chave</b> deste registro. Trocá-la repinta todo componente que a usa, em
            todos os projetos — não é o mesmo que corrigir o texto.
          </span>

          {trocandoAberto ? (
            <>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="color"
                  value={/^#[0-9a-f]{6}$/i.test(novaCor) ? novaCor : c.cor}
                  onChange={(e) => setNovaCor(e.target.value.toUpperCase())}
                  aria-label="Cor nova"
                  style={{ width: 40, height: 30, padding: 0, border: "1px solid var(--line2)", borderRadius: 6 }}
                />
                <input
                  value={novaCor}
                  onChange={(e) => setNovaCor(e.target.value.toUpperCase())}
                  placeholder="#RRGGBB"
                  aria-label="Cor nova em hexadecimal"
                  style={{ ...entrada, width: 120, fontFamily: "var(--fm)" }}
                />
              </span>
              <span style={{ display: "flex", gap: 7 }}>
                <button
                  onClick={() => aoTrocar(novaCor)}
                  disabled={!/^#[0-9a-f]{6}$/i.test(novaCor) || trocando}
                  style={{
                    height: 30, padding: "0 12px", borderRadius: 7, fontSize: 12, fontWeight: 600,
                    background: /^#[0-9a-f]{6}$/i.test(novaCor) ? "var(--acao)" : "var(--surface)",
                    color: /^#[0-9a-f]{6}$/i.test(novaCor) ? "var(--acao-ink)" : "var(--txt3)",
                  }}
                >
                  {trocando ? "Trocando…" : "Confirmar a troca"}
                </button>
                <button onClick={() => setTrocandoAberto(false)}
                        style={{ height: 30, padding: "0 10px", fontSize: 12, color: "var(--txt2)" }}>
                  Cancelar
                </button>
              </span>
            </>
          ) : (
            <button
              onClick={() => { setTrocandoAberto(true); setNovaCor(c.cor) }}
              style={{
                alignSelf: "flex-start", height: 28, padding: "0 11px", borderRadius: 7,
                border: "1px solid var(--acao)", background: "var(--surface)",
                fontSize: 12, fontWeight: 600, color: "var(--acao-on-soft)",
              }}
            >
              Trocar a cor
            </button>
          )}
        </div>

        {erro != null && (
          <div style={{
            padding: "10px 12px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
            background: erro instanceof ErroDeTrava ? "var(--acao-soft)" : "var(--erro-soft)",
            color: erro instanceof ErroDeTrava ? "var(--acao-on-soft)" : "var(--erro-on-soft)",
          }}>
            {erro instanceof ErroDaApi || erro instanceof Error ? erro.message : "Não foi possível gravar."}
          </div>
        )}
      </div>
    </aside>
  )
}

const entrada: React.CSSProperties = {
  width: "100%", height: 32, padding: "0 9px", borderRadius: 7,
  border: "1px solid var(--line2)", background: "var(--surface2)", fontSize: 12.5,
}

function Campo({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>{rotulo}</span>
      {children}
    </label>
  )
}
